import { Component, OnInit, signal, computed, WritableSignal, Signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { DateFnsModule } from 'ngx-date-fns';

import { JsUser, JsInvitee } from '../../_interfaces/user.interface';
import { InviteService } from '../../_services/invite.service';
import { AuthService } from '../../_services/auth.service';
import { StorageService } from '../../_services/storage.service';
import { AlertService } from '../../_services/alert.service';
import { FirestoreStoreService } from '../../_services/firestore-store.service';
import { FirebaseService } from '../../_services/firebase.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { serverTimestamp } from '@angular/fire/firestore';

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatDialogModule,
    MatTabsModule,
    MatChipsModule,
    DateFnsModule,
  ],
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.scss',
})
export class AdminComponent implements OnInit {
  private readonly ADMIN_EMAIL = 'joseph.manadan@realtime-innovations.com';

  private inviteService = inject(InviteService);
  private authService = inject(AuthService);
  private storageService = inject(StorageService);
  private alertService = inject(AlertService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private afs = inject(AngularFirestore);
  private store = inject(FirestoreStoreService);
  private fbs = inject(FirebaseService);

  // Use store for users and invitees data - automatically updates in real-time!
  allUsers = this.store.activeUsers;
  allInvitees = this.store.invitees;

  // Loading states from store
  isLoadingUsers = this.store.usersLoading;
  isLoadingInvitees = this.store.inviteesLoading;

  // Operation states
  isInviting: WritableSignal<boolean> = signal(false);
  isDeletingUser: WritableSignal<boolean> = signal(false);
  isBackfilling: WritableSignal<boolean> = signal(false);
  backfillResult: WritableSignal<any> = signal(null);

  // Form controls for invite
  inviteEmailControl = new FormControl('', [Validators.required, Validators.email]);
  inviteNameControl = new FormControl('');

  // Pending invitees only
  pendingInvitees = this.store.pendingInvitees;

  ngOnInit(): void {
    // No need to fetch - store is automatically initialized by AppComponent!
  }

  /**
   * Send invite to a new user
   */
  async sendInvite(): Promise<void> {
    if (this.inviteEmailControl.invalid) {
      this.snackBar.open('Please enter a valid email address', 'Close', {
        duration: 3000,
      });
      return;
    }

    const email = this.inviteEmailControl.value?.trim() || '';
    const name = this.inviteNameControl.value?.trim() || '';

    // Validate domain
    const domain = email.split('@')[1] || '';
    if (domain !== 'realtime-innovations.com') {
      this.snackBar.open(
        'Only @realtime-innovations.com email addresses can be invited',
        'Close',
        { duration: 4000 }
      );
      return;
    }

    // Check if already a user
    const existingUser = this.allUsers().find(
      (u) => u.email?.toLowerCase() === email.toLowerCase()
    );
    if (existingUser) {
      this.snackBar.open('This user already has an account', 'Close', {
        duration: 3000,
      });
      return;
    }

    this.isInviting.set(true);

    try {
      const currentUser = this.storageService.getUser();
      await this.inviteService.createInvite(
        { email, name },
        currentUser?.uid || '',
        currentUser?.email || ''
      );

      this.snackBar.open(`Invitation sent to ${email}`, 'Close', {
        duration: 3000,
      });

      // Clear form
      this.inviteEmailControl.reset();
      this.inviteNameControl.reset();
    } catch (error: any) {
      console.error('Error sending invite:', error);
      this.snackBar.open(
        error.message || 'Failed to send invitation',
        'Close',
        { duration: 4000 }
      );
    } finally {
      this.isInviting.set(false);
    }
  }

  /**
   * Resend invite email
   */
  async resendInvite(invitee: JsInvitee): Promise<void> {
    try {
      this.inviteService.resendInviteEmail(invitee.email, invitee.name).subscribe({
        next: () => {
          this.snackBar.open(`Invitation resent to ${invitee.email}`, 'Close', {
            duration: 3000,
          });
        },
        error: (error) => {
          console.error('Error resending invite:', error);
          this.snackBar.open('Failed to resend invitation', 'Close', {
            duration: 3000,
          });
        },
      });
    } catch (error) {
      console.error('Error resending invite:', error);
    }
  }

  /**
   * Delete/revoke an invite
   */
  async deleteInvite(invitee: JsInvitee): Promise<void> {
    const confirmed = await this.alertService.confirm(
      'Revoke Invitation',
      `Are you sure you want to revoke the invitation for ${invitee.email}? They will no longer be able to access the application.`,
      'Revoke',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      await this.inviteService.deleteInvitee(invitee.id);
      this.snackBar.open('Invitation revoked', 'Close', { duration: 3000 });
    } catch (error) {
      console.error('Error deleting invite:', error);
      this.snackBar.open('Failed to revoke invitation', 'Close', {
        duration: 3000,
      });
    }
  }

  /**
   * Check if user can be deleted (not admin)
   */
  canDeleteUser(user: JsUser): boolean {
    return user.email !== this.ADMIN_EMAIL;
  }

  /**
   * Delete a user (soft delete by setting deletedAt)
   */
  async deleteUser(user: JsUser): Promise<void> {
    if (!this.canDeleteUser(user)) {
      this.snackBar.open('Cannot delete admin user', 'Close', { duration: 3000 });
      return;
    }

    const confirmed = await this.alertService.confirm(
      'Remove User',
      `Are you sure you want to remove ${user.name || user.email}? They will no longer be able to access the application.`,
      'Remove',
      'Cancel'
    );

    if (!confirmed) return;

    this.isDeletingUser.set(true);

    try {
      // Soft delete by setting deletedAt
      await this.afs.collection('users').doc(user.id).update({
        deletedAt: serverTimestamp(),
        cloudUpdatedAt: serverTimestamp(),
      });

      // Also delete from invitees collection if exists
      const invitee = this.allInvitees().find(
        (i) => i.email.toLowerCase() === user.email.toLowerCase()
      );
      if (invitee) {
        await this.inviteService.deleteInvitee(invitee.id);
      }

      this.snackBar.open(`${user.name || user.email} has been removed`, 'Close', {
        duration: 3000,
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      this.snackBar.open('Failed to remove user', 'Close', { duration: 3000 });
    } finally {
      this.isDeletingUser.set(false);
    }
  }

  /**
   * Format date for display
   */
  formatDate(date: Date | null | undefined): string {
    if (!date) return 'Never';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Get user initials for avatar fallback
   */
  getUserInitials(name: string): string {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  /**
   * Track by functions for ngFor
   */
  trackByUser(index: number, user: JsUser): string {
    return user.id;
  }

  trackByInvitee(index: number, invitee: JsInvitee): string {
    return invitee.id;
  }

  /**
   * Run backfill to import existing SES templates into Firestore
   * This is used to migrate templates from the old app version
   */
  async runBackfill(dryRun: boolean = true): Promise<void> {
    this.isBackfilling.set(true);
    this.backfillResult.set(null);

    interface BackfillResult {
      success: boolean;
      message?: string;
      data?: {
        totalTemplates: number;
        groups: number;
        skipped: number;
        created: number;
        dryRun: boolean;
        templates: Array<{ templateName: string; languages: string[] }>;
      };
    }

    try {
      const result = await this.fbs.callableFunction('backfillEmailsFromSES', {
        prefix: 'me-',
        dryRun: dryRun
      }) as BackfillResult;

      this.backfillResult.set(result);

      if (result.success) {
        const message = dryRun
          ? `Dry run complete: ${result.data?.created || 0} templates would be created`
          : `Backfill complete: ${result.data?.created || 0} templates created`;
        this.snackBar.open(message, 'Close', { duration: 5000 });
      } else {
        this.snackBar.open(result.message || 'Backfill failed', 'Close', { duration: 5000 });
      }
    } catch (error: any) {
      console.error('Error running backfill:', error);
      this.snackBar.open(error.message || 'Failed to run backfill', 'Close', { duration: 5000 });
      this.backfillResult.set({ success: false, message: error.message });
    } finally {
      this.isBackfilling.set(false);
    }
  }
}
